import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faEmptySet } from '@fortawesome/pro-regular-svg-icons'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Button } from 'antd'
import { Icon, Input, Popover, T, VStack } from 'govwell-ui'
import { FontAwesomeIconKeys, getIconKeyFromString, IconKey } from 'govwell-ui/components/Icon/util'

import EmptyState from 'src/components/EmptyState'
import useDisclosure from 'src/hooks/use-disclosure'

type Props = {
  onValueChange: (value: IconKey) => void
  value: IconKey
}
const IconSelect = ({ onValueChange, value }: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const { isOpen, open: onOpen, close: onClose } = useDisclosure()
  const parentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen])

  const matchingIcons = useMemo(
    () =>
      FontAwesomeIconKeys.filter((icon) =>
        icon.substring(2).toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [searchQuery]
  )

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(matchingIcons.length / 10), // 10 icons per row
    getScrollElement: () => parentRef.current,
    estimateSize: () => 34,
  })

  const handleIconSelected = useCallback(
    (icon: IconKey) => {
      onValueChange(icon)
      onClose()
    },
    [onClose, onValueChange]
  )

  return (
    <Popover isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
      <Popover.Trigger>
        <Button className="!flex w-[32px] !items-center !justify-center !px-2">
          <T.BodyLarge>
            <Icon icon={value || 'FaQuestion'} />
          </T.BodyLarge>
        </Button>
      </Popover.Trigger>
      <Popover.Content align="start" side="bottom" className="p-2" sideOffset={4}>
        <VStack gap={2} maxHeight="220px" width="300px">
          <Input
            ariaLabel="Search for an icon"
            placeholder="Search for an icon"
            value={searchQuery}
            onValueChange={setSearchQuery}
            size="sm"
          />
          <div
            className="relative w-full overflow-x-hidden overflow-y-auto"
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
            ref={parentRef}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => (
              <div
                key={virtualItem.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <div className="grid grid-cols-10 gap-2 p-2">
                  {matchingIcons
                    .slice(virtualItem.index * 10, (virtualItem.index + 1) * 10)
                    .map((icon) => (
                      <button
                        key={icon}
                        className="flex h-[24px] w-[24px] cursor-pointer items-center justify-center rounded-md p-1 hover:bg-neutral-200 focus-visible:outline-1 active:bg-neutral-200"
                        onClick={() => handleIconSelected(getIconKeyFromString(icon))}
                        title={icon}
                      >
                        <Icon icon={getIconKeyFromString(icon)} />
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
          {!matchingIcons.length && (
            <EmptyState hideBorder size="sm">
              <EmptyState.Image icon={faEmptySet} />
              <EmptyState.Message>No icons found</EmptyState.Message>
            </EmptyState>
          )}
        </VStack>
      </Popover.Content>
    </Popover>
  )
}

export default React.memo(IconSelect)
